/**
 * HTTPS Utility Functions
 * Ensures all URLs use HTTPS protocol for security
 */

/**
 * Ensures a URL uses HTTPS protocol
 * @param url - The URL to convert to HTTPS
 * @returns The URL with HTTPS protocol
 */
export const ensureHttps = (url: string): string => {
  if (!url) return url;
  
  // If URL already starts with https://, return as is
  if (url.startsWith('https://')) {
    return url;
  }
  
  // If URL starts with http://, replace with https://
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  
  // If URL doesn't have a protocol, add https://
  if (!url.startsWith('//')) {
    return `https://${url}`;
  }
  
  return url;
};

/**
 * Gets the backend URL with HTTPS enforcement
 * @returns The backend URL with HTTPS protocol
 */
export const getBackendUrl = (): string => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
  
  // For localhost development, allow HTTP
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    return baseUrl;
  }
  
  // For production, enforce HTTPS
  return ensureHttps(baseUrl);
};

/**
 * Creates a full URL with HTTPS enforcement
 * @param path - The API path
 * @returns The full HTTPS URL
 */
export const createHttpsUrl = (path: string): string => {
  const baseUrl = getBackendUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Validates if a URL is secure (HTTPS)
 * @param url - The URL to validate
 * @returns True if the URL is secure (HTTPS)
 */
export const isSecureUrl = (url: string): boolean => {
  return url.startsWith('https://');
};

/**
 * Forces HTTPS redirect for the current page if not already HTTPS
 * This should be called in the main App component or index.tsx
 */
export const enforceHttpsRedirect = (): void => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;
  
  // Only redirect if not already on HTTPS
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    const httpsUrl = `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(httpsUrl);
  }
};
